import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ExternalLink } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from "recharts";
import FunderShell from "./FunderShell";
import { SectionCard, StatusBadge, SCORE_DIMS, composite } from "./FunderUI";
import { getComparisonPool } from "../../service/FunderPortalService";

const SLOT_COLORS = ["var(--series-blue)", "var(--brand-text)", "var(--bronze)"];

// Strong if the proposal's cluster IS the priority; partial if they share a
// meaningful word; otherwise none.
function alignLevel(cluster, priority) {
    if (!cluster || !priority) return "none";
    if (cluster.toLowerCase() === priority.toLowerCase()) return "strong";
    const words = priority.toLowerCase().split(/\s+/).filter((w) => w.length > 4);
    const c = cluster.toLowerCase();
    return words.some((w) => c.includes(w)) ? "partial" : "none";
}

function AlignmentCell({ level }) {
    if (level === "strong") return <span style={{ color: "var(--success)", fontSize: 18, lineHeight: 1 }}>✓</span>;
    if (level === "partial") return <span style={{ color: "#d4a017", fontSize: 18, lineHeight: 1 }}>◐</span>;
    return <span style={{ color: "var(--ink-250)", fontSize: 18, lineHeight: 1 }}>—</span>;
}

const strongestDim = (p) => {
    const dims = [["novelty", "Novelty"], ["methodology", "Methods"], ["impact", "Impact"]];
    return dims.reduce((best, d) => (p[d[0]] > p[best[0]] ? d : best))[1];
};

function Inner({ callId, call }) {
    const navigate = useNavigate();
    const pool = getComparisonPool(callId);

    const defaultIds = [...pool].sort((a, b) => composite(b) - composite(a)).slice(0, 3).map((p) => p.id);
    const [selectedIds, setSelectedIds] = useState(defaultIds);

    const selected = selectedIds.map((id) => pool.find((p) => p.id === id)).filter(Boolean);
    const available = pool.filter((p) => !selectedIds.includes(p.id));

    const removeAt = (id) => setSelectedIds((prev) => prev.filter((x) => x !== id));
    const add = (id) => setSelectedIds((prev) => (prev.length >= 3 || !id ? prev : [...prev, Number.isNaN(+id) ? id : +id]));
    const reset = () => setSelectedIds(defaultIds);

    const radarData = SCORE_DIMS.map((d) => {
        const row = { dimension: d.label };
        selected.forEach((p) => { row[p.id] = p[d.key]; });
        return row;
    });

    const colorFor = (p) => SLOT_COLORS[selected.indexOf(p)] || "var(--ink-400)";

    return (
        <div className="funder-section-stack">
            {/* Header */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start", justifyContent: "space-between" }}>
                <div>
                    <h1 className="upload-page-heading" style={{ margin: 0 }}>Funder Comparison</h1>
                    <p className="upload-page-meta" style={{ margin: "4px 0 0" }}>
                        Comparing {selected.length} {selected.length === 1 ? "proposal" : "proposals"} from {call.round}. Add up to three.
                    </p>
                </div>
                <button className="funder-btn-ghost" onClick={reset}>Reset to top 3</button>
            </div>

            {/* Selected proposal cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 14 }}>
                {selected.map((p) => (
                    <div key={p.id} className="funder-card" style={{ borderTop: `3px solid ${colorFor(p)}` }}>
                        <div className="funder-card-body">
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ink-350)" }}>
                                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: colorFor(p) }} />
                                    {p.grantNumber}
                                </span>
                                <button onClick={() => removeAt(p.id)} aria-label="Remove" style={{ border: "none", background: "none", cursor: "pointer", color: "var(--ink-250)" }}><X size={15} /></button>
                            </div>
                            <div
                                onClick={() => navigate(`/funder/proposal?id=${p.id}`)}
                                style={{ fontSize: 14, fontWeight: 650, color: "var(--ink-900)", lineHeight: 1.35, cursor: "pointer", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                            >
                                {p.title}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 6 }}>{p.pi}</div>
                            <div style={{ fontSize: 12, color: "var(--ink-350)" }}>{p.institution}</div>
                            <div style={{ fontSize: 12, color: "var(--ink-350)", marginTop: 2 }}>{p.amountRequested} · {p.durationYears} yrs</div>
                            <span className="funder-link" style={{ marginTop: 10, fontSize: 12 }} onClick={() => navigate(`/funder/proposal?id=${p.id}`)}>
                                View full detail <ExternalLink size={12} />
                            </span>
                        </div>
                    </div>
                ))}
                {selected.length < 3 && (
                    <div className="funder-card" style={{ borderStyle: "dashed", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 150 }}>
                        <div style={{ textAlign: "center", padding: 16 }}>
                            <select className="funder-select" defaultValue="" onChange={(e) => { add(e.target.value); e.target.value = ""; }} aria-label="Add proposal to comparison">
                                <option value="" disabled>+ Add proposal…</option>
                                {available.map((p) => (
                                    <option key={p.id} value={p.id}>{p.grantNumber} — {p.title.slice(0, 48)}{p.title.length > 48 ? "…" : ""}</option>
                                ))}
                            </select>
                            <div style={{ fontSize: 11, color: "var(--ink-300)", marginTop: 8 }}>Compare up to 3</div>
                        </div>
                    </div>
                )}
            </div>

            {selected.length < 2 ? (
                <div className="funder-empty">Add at least two proposals to compare.</div>
            ) : (
                <>
                    {/* Radar + dimension bars */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
                        <SectionCard kicker="Score Signature" title="DeepThought scores — radar overlay">
                            <div style={{ width: "100%", height: 300 }}>
                                <ResponsiveContainer>
                                    <RadarChart data={radarData}>
                                        <PolarGrid stroke="var(--border)" />
                                        <PolarAngleAxis dataKey="dimension" tick={{ fill: "var(--ink-500)", fontSize: 12 }} />
                                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "var(--ink-300)", fontSize: 9 }} stroke="var(--border)" />
                                        {selected.map((p) => (
                                            <Radar key={p.id} name={p.grantNumber} dataKey={p.id} stroke={colorFor(p)} fill={colorFor(p)} fillOpacity={0.14} strokeWidth={1.8} />
                                        ))}
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)" }} />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                        </SectionCard>

                        <SectionCard kicker="Dimension Breakdown" title="Side-by-side scores">
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                {SCORE_DIMS.map((d) => (
                                    <div key={d.key}>
                                        <div style={{ fontSize: 12, color: "var(--ink-900)", marginBottom: 6, fontWeight: 600 }}>{d.label}</div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            {selected.map((p) => (
                                                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: colorFor(p), flexShrink: 0 }} />
                                                    <div className="funder-bar-track" style={{ flex: 1 }}>
                                                        <div className="funder-bar-fill" style={{ width: `${p[d.key]}%`, background: colorFor(p) }} />
                                                    </div>
                                                    <span style={{ width: 26, textAlign: "right", fontSize: 12, fontWeight: 700, color: colorFor(p) }}>{p[d.key]}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </SectionCard>
                    </div>

                    {/* Alignment matrix */}
                    <SectionCard kicker="Alignment to Call Priorities" title="How each proposal maps to the call">
                        <div style={{ overflowX: "auto" }}>
                            <table className="funder-table">
                                <thead>
                                    <tr>
                                        <th>Priority</th>
                                        {selected.map((p) => (
                                            <th key={p.id} style={{ textAlign: "center" }}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: colorFor(p) }} />
                                                    {p.grantNumber.replace("HF-", "")}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {call.priorities.map((prio) => (
                                        <tr key={prio}>
                                            <td>{prio}</td>
                                            {selected.map((p) => (
                                                <td key={p.id} style={{ textAlign: "center" }}><AlignmentCell level={alignLevel(p.cluster, prio)} /></td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 14, fontSize: 11, color: "var(--ink-350)" }}>
                            <span><span style={{ color: "var(--success)" }}>✓</span> Strong</span>
                            <span><span style={{ color: "#d4a017" }}>◐</span> Partial</span>
                            <span><span style={{ color: "var(--ink-250)" }}>—</span> None</span>
                        </div>
                    </SectionCard>

                    {/* DeepThought differentiator */}
                    <SectionCard kicker="Key Differentiators" title="DeepThought-synthesised comparison">
                        <p style={{ fontSize: 14, color: "var(--ink-750)", lineHeight: 1.6, margin: 0 }}>
                            {selected.map((p, i) => (
                                <span key={p.id}>
                                    <span style={{ color: colorFor(p), fontWeight: 700 }}>{p.grantNumber}</span>
                                    {" "}leads on <strong>{strongestDim(p)}</strong> (composite {composite(p)}) and is positioned in the <em>{p.cluster}</em> cluster{p.remit && p.remit.state !== "in" ? `, though its scope fit is "${p.remit.state}"` : ""}.{i < selected.length - 1 ? " " : ""}
                                </span>
                            ))}
                        </p>
                        <p style={{ fontSize: 11.5, color: "var(--ink-350)", fontStyle: "italic", marginTop: 10, marginBottom: 0 }}>
                            DeepThought synthesis focuses reviewer attention; it is not a decision.
                        </p>
                    </SectionCard>

                    {/* Review status */}
                    <SectionCard kicker="Review Status" title="Current status of each proposal">
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
                            {selected.map((p) => (
                                <div key={p.id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--ink-500)" }}>
                                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: colorFor(p) }} />
                                        {p.grantNumber}
                                    </span>
                                    <div><StatusBadge status={p.status} /></div>
                                    <div style={{ fontSize: 12, color: "var(--ink-400)" }}>Reviewer: {p.assigned || "Unassigned"}</div>
                                    <span className="funder-link" style={{ fontSize: 12 }} onClick={() => navigate(`/funder/proposal?id=${p.id}`)}>Open proposal →</span>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </>
            )}
        </div>
    );
}

export default function FunderComparison() {
    return (
        <FunderShell activePage="Funder Comparison">
            {(callId, call) => <Inner callId={callId} call={call} />}
        </FunderShell>
    );
}
